// ...existing code...
import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import useAddAdSegment from "@entities/brief/lib/hooks/darfting/useAddAdSegment";
import useGetMediaAssets from "@entities/brief/lib/hooks/darfting/useGetMediaAssets";
import { ToastService } from "@shared/lib/services";
import {
  IDraftingComments,
  IDraftingDrawings,
  IDraftingDrawingsPoint,
  IDraftingMediaAssets,
} from "@shared/lib/types/drafting";
import { Button } from "@shared/ui/Button";
import { Card, Flex, Spin } from "antd";
import {
  AlertCircle,
  Check,
  GripHorizontal,
  Loader2,
  Pause,
  Play,
  Scissors,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface VideoPlayerProps {
  asset: IDraftingMediaAssets;
  comments: IDraftingComments[];
  currentTime: number;
  onTimeUpdate: (time: number) => void;
  onAddCommentRequest: (time: number) => void;
  isDrawingMode: boolean;
  draftDrawings: IDraftingDrawings[];
  onUpdateDraftDrawings: (drawings: IDraftingDrawings[]) => void;
  activeDrawing: IDraftingDrawings[] | null;
  pauseRequestId?: number | null;
  draftingId: string;
  draftingVersionId: string;
  mediaAssetId: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  asset,
  comments,
  currentTime,
  onTimeUpdate,
  onAddCommentRequest,
  isDrawingMode,
  draftDrawings,
  onUpdateDraftDrawings,
  activeDrawing,
  pauseRequestId,
  draftingId,
  draftingVersionId,
  mediaAssetId,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const ytPlayerRef = useRef<any>(null); // Reference to YT Player instance
  const ytContainerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isHoveringTimeline, setIsHoveringTimeline] = useState(false);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ytError, setYtError] = useState<boolean>(false);
  const [mediaAspect, setMediaAspect] = useState<number>(16 / 9);
  const [showTranscription, setShowTranscription] = useState<boolean>(false);
  const [showSegmentControls, setShowSegmentControls] =
    useState<boolean>(false);

  const {
    mediaAssetsInfo,
    isMediaAssetsFetching,
    isMediaAssetsFetched,
    mediaAssetsRefetch,
  } = useGetMediaAssets({
    draftingId,
    mediaAssetId,
    draftingVersionId,
  });
  const [addSegment, setAddSegment] = useState<{
    from: number | null;
    to: number | null;
  }>({
    from: mediaAssetsInfo?.data?.adSegements?.start || null,
    to: mediaAssetsInfo?.data?.adSegements?.end || null,
  });

  const {
    addAdSegment,
    isAddAdSegmentLoading,
    isAddAdSegmentSuccess,
    addAdSegmentError,
    addAdSegmentVariables,
  } = useAddAdSegment();

  const transcription = mediaAssetsInfo?.data?.transcription;

  const transcriptionLoading =
    mediaAssetsInfo?.data?.transcriptionStatus === "Pending";

  const hasTranscription = (transcription?.length ?? 0) > 0;

  const seekTo = (time: number) => {
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
      videoRef.current
    ) {
      videoRef.current.currentTime = time;
    } else if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
      ytPlayerRef.current &&
      typeof ytPlayerRef.current.seekTo === "function"
    ) {
      ytPlayerRef.current.seekTo(time, true);
    }
  };

  const ytElementId = useMemo(() => `youtube-player-${asset._id}`, [asset._id]); // width / height

  // Current stroke being drawn
  const [currentPoints, setCurrentPoints] = useState<IDraftingDrawingsPoint[]>(
    []
  );

  // --- YouTube Logic ---
  const extractYoutubeId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Keep addSegment values in-range when duration changes
  // useEffect(() => {
  //   setAddSegment((prev) => {
  //     const dur = Number.isFinite(duration) ? duration : 0;
  //     let from = prev.from ?? 0;
  //     let to = prev.to ?? dur;

  //     // Clamp
  //     if (from < 0) from = 0;
  //     if (to < 0) to = 0;
  //     if (from > dur) from = dur;
  //     if (to > dur) to = dur;

  //     // Ensure from <= to
  //     if (from > to) from = to;

  //     // Only update if different to avoid re-renders
  //     if (from === prev.from && to === prev.to) return prev;
  //     return { ...prev, from, to };
  //   });
  // }, [duration]);

  useEffect(() => {
    setAddSegment((prev) => ({
      ...prev,
      from: mediaAssetsInfo?.data?.adSegements?.start || null,
      to: mediaAssetsInfo?.data?.adSegements?.end || null,
    }));
  }, [draftingId, draftingVersionId, mediaAssetId, mediaAssetsInfo]);

  useEffect(() => {
    if (addAdSegmentError) {
      ToastService.error(addAdSegmentError?.message);
    }
    if (isAddAdSegmentSuccess) {
      ToastService.success("Ad segment added successfully.");
      // Keep controls open after saving
    }
  }, [isAddAdSegmentSuccess, addAdSegmentError]);

  useEffect(() => {
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.Image ||
      asset.type === BriefDeliverableTaskUploadTypeEnum.MediaLink
    ) {
      setShowTranscription(false);
      setShowSegmentControls(false);
    }
  }, [asset]);

  // Check if segment is saved
  const hasSavedSegment = useMemo(() => {
    return !!(
      mediaAssetsInfo?.data?.adSegements?.start !== undefined &&
      mediaAssetsInfo?.data?.adSegements?.end !== undefined
    );
  }, [mediaAssetsInfo?.data?.adSegements]);

  // Initialize addSegment from saved segment when available
  useEffect(() => {
    if (
      mediaAssetsInfo?.data?.adSegements?.start !== undefined &&
      mediaAssetsInfo?.data?.adSegements?.end !== undefined
    ) {
      setAddSegment({
        from: mediaAssetsInfo.data.adSegements.start,
        to: mediaAssetsInfo.data.adSegements.end,
      });
    } else if (!showSegmentControls) {
      // Only reset if controls are not shown (to preserve user input when editing)
      setAddSegment({
        from: null,
        to: null,
      });
    }
  }, [mediaAssetsInfo?.data?.adSegements, showSegmentControls]);

  const handleSaveSegment = () => {
    if (addSegment.from !== null && addSegment.to !== null) {
      addAdSegment({
        draftingId,
        draftingVersionId,
        mediaAssetId,
        adSegments: {
          start: addSegment.from,
          end: addSegment.to,
        },
      });
      // Close controls after saving
      setShowSegmentControls(false);
    }
  };

  const handleSetIntegrationStart = () => {
    setAddSegment((prev) => ({
      ...prev,
      from: currentTime,
    }));
  };

  const handleSetIntegrationEnd = () => {
    if (addSegment.from !== null) {
      setAddSegment((prev) => ({
        ...prev,
        to: currentTime,
      }));
    }
  };

  const handleClearIntegration = () => {
    setAddSegment({
      from: null,
      to: null,
    });
    // Close controls after clearing
    setShowSegmentControls(false);
  };

  useEffect(() => {
    // Reset state on asset change
    setDuration(0);
    setIsPlaying(false);
    setYtError(false);

    // Cleanup previous player if exists (safety check)
    if (ytPlayerRef.current) {
      try {
        if (ytPlayerRef.current.destroy) {
          ytPlayerRef.current.destroy();
        }
      } catch (e) {
        console.error("Error destroying player", e);
      }
      ytPlayerRef.current = null;
    }

    let interval: NodeJS.Timeout | null = null;

    if (asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink) {
      const videoId = extractYoutubeId(asset.url);
      if (!videoId) {
        setYtError(true);
        return;
      }

      const initPlayer = () => {
        // Check if container exists
        if (!ytContainerRef.current) return;

        // Prevent multiple initializations for the same ID if race condition occurs
        if (ytPlayerRef.current) return;

        // Clear container to ensure clean slate
        ytContainerRef.current.innerHTML = "";
        const placeholder = document.createElement("div");
        placeholder.id = ytElementId;
        ytContainerRef.current.appendChild(placeholder);

        ytPlayerRef.current = new window.YT.Player(placeholder, {
          videoId: videoId,
          width: "100%",
          height: "100%",
          playerVars: {
            controls: 0,
            disablekb: 1, // Disable keyboard controls
            fs: 0, // Disable fullscreen button
            modestbranding: 1,
            rel: 0,
            showinfo: 0, // Deprecated but good to keep
            iv_load_policy: 3, // Hide annotations
            origin: window.location.origin,
            playsinline: 1,
            // Required for loop to work
            autohide: 1, // Hide controls when playing
          },
          events: {
            onReady: (event: any) => {
              setDuration(event.target.getDuration());
            },
            onStateChange: (event: any) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                setIsPlaying(true);
              } else if (
                event.data === window.YT.PlayerState.PAUSED ||
                event.data === window.YT.PlayerState.ENDED
              ) {
                setIsPlaying(false);
              }
            },
            onError: (e: any) => {
              setYtError(true);
            },
          },
        });
      };

      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        window.onYouTubeIframeAPIReady = () => {
          initPlayer();
        };
      } else {
        if (window.YT && window.YT.Player) {
          initPlayer();
        } else {
          window.onYouTubeIframeAPIReady = initPlayer;
        }
      }

      interval = setInterval(() => {
        if (
          ytPlayerRef.current &&
          typeof ytPlayerRef.current.getCurrentTime === "function"
        ) {
          try {
            const time = ytPlayerRef.current.getCurrentTime();
            onTimeUpdate(time);
          } catch (e) {
            // Ignore errors during disposal
          }
        }
      }, 500);

      return () => {
        if (interval) clearInterval(interval);
        if (ytPlayerRef.current) {
          try {
            if (ytPlayerRef.current.destroy) {
              ytPlayerRef.current.destroy();
            }
          } catch (e) {
            console.error("Error destroying player in cleanup", e);
          }
          ytPlayerRef.current = null;
        }
        if (ytContainerRef.current) {
          ytContainerRef.current.innerHTML = "";
        }
      };
    }
  }, [asset._id, asset.type, asset.url, ytElementId]);

  // Sync external currentTime prop with video/YT
  useEffect(() => {
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
      videoRef.current &&
      Math.abs(videoRef.current.currentTime - currentTime) > 0.5
    ) {
      videoRef.current.currentTime = currentTime;
    }
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
      ytPlayerRef.current &&
      typeof ytPlayerRef.current.seekTo === "function" &&
      typeof ytPlayerRef.current.getCurrentTime === "function"
    ) {
      const playerTime = ytPlayerRef.current.getCurrentTime();
      if (Math.abs(playerTime - currentTime) > 1.0) {
        ytPlayerRef.current.seekTo(currentTime, true);
      }
    }
  }, [currentTime, asset.type]);

  // External pause requests (e.g., when selecting a comment)
  useEffect(() => {
    if (!pauseRequestId) return;
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
      videoRef.current
    ) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
      ytPlayerRef.current &&
      typeof ytPlayerRef.current.pauseVideo === "function"
    ) {
      ytPlayerRef.current.pauseVideo();
      setIsPlaying(false);
    }
  }, [pauseRequestId, asset.type]);

  // Pause when entering drawing mode
  useEffect(() => {
    if (isDrawingMode && isPlaying) {
      if (
        asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
        videoRef.current
      ) {
        videoRef.current.pause();
      } else if (
        asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
        ytPlayerRef.current &&
        typeof ytPlayerRef.current.pauseVideo === "function"
      ) {
        ytPlayerRef.current.pauseVideo();
      }
      setIsPlaying(false);
    }
  }, [isDrawingMode, asset.type]);

  const getMediaRect = () => {
    const container = containerRef.current;
    if (!container) return { left: 0, top: 0, width: 0, height: 0 };
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (!cw || !ch) return { left: 0, top: 0, width: cw, height: ch };
    const containerAspect = cw / ch;
    let width = cw;
    let height = ch;
    let left = 0;
    let top = 0;
    if (containerAspect > mediaAspect) {
      height = ch;
      width = height * mediaAspect;
      left = (cw - width) / 2;
    } else {
      width = cw;
      height = width / mediaAspect;
      top = (ch - height) / 2;
    }
    return { left, top, width, height };
  };

  // Canvas Drawing Logic
  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    renderDrawings();
  };

  const renderDrawings = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const strokesToDraw = isDrawingMode
      ? draftDrawings
      : activeDrawing || draftDrawings;
    const mediaRect = getMediaRect();
    if (mediaRect.width === 0 || mediaRect.height === 0) return;

    strokesToDraw.forEach((stroke) => {
      if (stroke.points.length < 2) return;

      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const startX = stroke.points[0].x * mediaRect.width + mediaRect.left;
      const startY = stroke.points[0].y * mediaRect.height + mediaRect.top;

      ctx.moveTo(startX, startY);

      for (let i = 1; i < stroke.points.length; i++) {
        const x = stroke.points[i].x * mediaRect.width + mediaRect.left;
        const y = stroke.points[i].y * mediaRect.height + mediaRect.top;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    });

    if (currentPoints.length > 0) {
      ctx.beginPath();
      ctx.strokeStyle = "#ef4444";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const startX = currentPoints[0].x * mediaRect.width + mediaRect.left;
      const startY = currentPoints[0].y * mediaRect.height + mediaRect.top;

      ctx.moveTo(startX, startY);
      for (let i = 1; i < currentPoints.length; i++) {
        const x = currentPoints[i].x * mediaRect.width + mediaRect.left;
        const y = currentPoints[i].y * mediaRect.height + mediaRect.top;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  };



  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    renderDrawings();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [
    draftDrawings,
    activeDrawing,
    currentPoints,
    asset.type,
    mediaAspect,
    isDrawingMode,
  ]);

  // Utility to get coordinates from both mouse and touch events
  const getCoordinates = (
    e: React.MouseEvent | React.TouchEvent,
    element: HTMLElement
  ) => {
    const rect = element.getBoundingClientRect();
    let clientX: number, clientY: number;

    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    const mediaRect = getMediaRect();
    const normX = (clientX - rect.left - mediaRect.left) / mediaRect.width;
    const normY = (clientY - rect.top - mediaRect.top) / mediaRect.height;

    return {
      x: Math.max(0, Math.min(1, normX)),
      y: Math.max(0, Math.min(1, normY)),
    };
  };

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawingMode) return;
    if ("touches" in e) e.preventDefault();

    setIsDrawing(true);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCoordinates(e, canvas);
    setCurrentPoints([{ x, y }]);
  };

  const draw = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawing || !isDrawingMode) return;
    if ("touches" in e) e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCoordinates(e, canvas);
    setCurrentPoints((prev) => [...prev, { x, y }]);
  };

  const endDrawing = () => {
    if (!isDrawing || !isDrawingMode) return;
    setIsDrawing(false);

    if (currentPoints.length > 1) {
      const newStroke: IDraftingDrawings = {
        points: currentPoints,
        color: "#ef4444",
      };
      onUpdateDraftDrawings([...draftDrawings, newStroke]);
    }
    setCurrentPoints([]);
  };

  const togglePlay = () => {
    if (asset.type === BriefDeliverableTaskUploadTypeEnum.Image) return;
    if (isDrawingMode) return;

    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
      videoRef.current
    ) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise
            .then(() => setIsPlaying(true))
            .catch((err) => {
              // Ignore play interruptions (e.g., immediate pause)
              console.warn("Play interrupted", err);
            });
        } else {
          setIsPlaying(true);
        }
      }
    } else if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
      ytPlayerRef.current &&
      typeof ytPlayerRef.current.playVideo === "function"
    ) {
      if (isPlaying) {
        ytPlayerRef.current.pauseVideo();
        setIsPlaying(false);
      } else {
        try {
          ytPlayerRef.current.playVideo();
          setIsPlaying(true);
        } catch (err) {
          console.warn("YT play interrupted", err);
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      onTimeUpdate(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      if (videoRef.current.videoWidth && videoRef.current.videoHeight) {
        setMediaAspect(
          videoRef.current.videoWidth / videoRef.current.videoHeight
        );
      }
    }
  };

  const formatTime = (time: number) => {
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min}:${sec.toString().padStart(2, "0")}`;
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      isDrawingMode ||
      asset.type === BriefDeliverableTaskUploadTypeEnum.Image
    )
      return;

    if (timelineRef.current && duration) {
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const newTime = percentage * duration;

      if (
        asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
        videoRef.current
      ) {
        videoRef.current.currentTime = newTime;
      } else if (
        asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
        ytPlayerRef.current &&
        typeof ytPlayerRef.current.seekTo === "function"
      ) {
        ytPlayerRef.current.seekTo(newTime, true);
      }

      onTimeUpdate(newTime);
      onAddCommentRequest(newTime);
      setIsPlaying(false);

      if (asset.type === BriefDeliverableTaskUploadTypeEnum.Video)
        videoRef.current?.pause();
      if (asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink)
        ytPlayerRef.current?.pauseVideo();
    }
  };

  const handleTimelineTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (
      isDrawingMode ||
      asset.type === BriefDeliverableTaskUploadTypeEnum.Image
    )
      return;
    setIsHoveringTimeline(true);
    handleTimelineTouchMove(e);
  };

  const handleTimelineTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (
      isDrawingMode ||
      asset.type === BriefDeliverableTaskUploadTypeEnum.Image
    )
      return;
    if (timelineRef.current && duration) {
      const rect = timelineRef.current.getBoundingClientRect();
      const touchX = e.touches[0].clientX;
      const x = touchX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const newTime = percentage * duration;

      setHoverTime(newTime);

      if (
        asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
        videoRef.current
      ) {
        videoRef.current.currentTime = newTime;
      } else if (
        asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
        ytPlayerRef.current &&
        typeof ytPlayerRef.current.seekTo === "function"
      ) {
        ytPlayerRef.current.seekTo(newTime, true);
      }
      onTimeUpdate(newTime);
    }
  };

  const handleTimelineTouchEnd = () => {
    if (
      isDrawingMode ||
      asset.type === BriefDeliverableTaskUploadTypeEnum.Image
    )
      return;
    setIsHoveringTimeline(false);
    if (hoverTime !== null) {
      onAddCommentRequest(hoverTime);
      setHoverTime(null);
    }
    setIsPlaying(false);
    if (asset.type === BriefDeliverableTaskUploadTypeEnum.Video)
      videoRef.current?.pause();
    if (asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink)
      ytPlayerRef.current?.pauseVideo();
  };

  const handleTimelineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (asset.type === BriefDeliverableTaskUploadTypeEnum.Image) return;
    if (timelineRef.current && duration) {
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      setHoverTime(percentage * duration);
    }
  };

  const toggleMute = () => {
    if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.Video &&
      videoRef.current
    ) {
      videoRef.current.muted = !isMuted;
    } else if (
      asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
      ytPlayerRef.current &&
      typeof ytPlayerRef.current.mute === "function"
    ) {
      if (!isMuted) ytPlayerRef.current.mute();
      else ytPlayerRef.current.unMute();
    }
    setIsMuted(!isMuted);
  };

  // Styles used to replace tailwind classes
  const styles: Record<string, any> = {
    // root becomes a two-column container: left = player, right = panel
    root: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
      background: "transparent",
      minHeight: 0,
    },
    // left pane holds the player area
    leftPane: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: "#000",
      position: "relative",
      userSelect: "none",
      boxSizing: "border-box",
      padding: 40,
    },
    // right panel styles
    rightPanel: {
      width: "30%",
      minWidth: 320,
      maxWidth: "40%",
      background: " rgb(17, 24, 39)",
      borderLeft: "1px solid rgba(0,0,0,0.08)",
      boxSizing: "border-box",
      padding: 16,
      overflowY: "auto",
    },
    containerMedia: () =>
      ({
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: "#000",
        width: "100%",
        maxWidth: "100%",
        flex: 1,
        minHeight: 0,

        // maxHeight: "calc(100vh - 250px)",
      } as React.CSSProperties),
    containerImage: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      background: "#000",
      width: "100%",
      maxWidth: "100%",
      flex: 1,
      minHeight: 0,
      // maxHeight: "calc(100vh - 150px)",
      // maxHeight: "95%",
    } as React.CSSProperties,
    videoImg: {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain" as const,
    },
    canvas: (isActive: boolean) =>
      ({
        position: "absolute",
        inset: 0,
        zIndex: 10,
        width: "100%",
        height: "100%",
        cursor: isActive ? "crosshair" : "default",
        pointerEvents: isActive ? "auto" : "none",
      } as React.CSSProperties),
    centerOverlay: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.2)",
      cursor: "pointer",
      pointerEvents: "auto",
    },
    playIconWrap: {
      background: "rgba(0,0,0,0.5)",
      padding: 16,
      borderRadius: "50%",
      border: "1px solid rgba(255,255,255,0.08)",
      boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
    },
    drawingBadge: {
      position: "absolute",
      top: 12,
      left: "50%",
      transform: "translateX(-50%)",
      background: "rgba(0,0,0,0.7)",
      color: "#fff",
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      display: "flex",
      alignItems: "center",
      gap: 8,
      zIndex: 20,
      border: "1px solid rgba(255,255,255,0.12)",
    },

    timelineWrapper: {
      position: "relative",
      height: 24,
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      width: "100%",
      boxSizing: "border-box",
      paddingLeft: 4,
      paddingRight: 4,
      overflow: "visible",
    },
    timelineTrack: {
      position: "absolute",
      left: 4,
      right: 4,
      height: 6,
      background: "#e5e7eb",
      borderRadius: 999,
      overflow: "hidden",
    },
    progressBar: (pct: number) =>
      ({
        height: "100%",
        background: "#1f6feb",
        transformOrigin: "left",
        width: `${pct}%`,
      } as React.CSSProperties),
    markerBase: (leftPct: number): React.CSSProperties => ({
      position: "absolute",
      top: "50%",
      transform: "translate(-50%, -50%)",
      left: `${leftPct}%`,
      borderRadius: 999,
      zIndex: 10,
    }),
    markerResolved: {
      background: "#10b981",
      height: 8,
      width: 4,
    },
    markerUnresolved: {
      background: "#f6c23e",
      height: 16,
      width: 6,
      boxShadow: "0 0 0 1px rgba(255,255,255,0.08) inset",
    },
    hoverGhost: (leftPct: number) => ({
      position: "absolute",
      left: `${leftPct}%`,
      top: 0,
      height: "100%",
      width: 2,
      background: "rgba(31,111,235,0.35)",
      pointerEvents: "none",
    }),
    controlsRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "#6b7280",
    },
    controlsLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    timeInfo: {
      display: "flex",
      alignItems: "center",
      fontFamily: "monospace",
      gap: 8,
      fontSize: 12,
      color: "#9ca3af",
    },
    volumeContainer: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    range: {
      width: 80,
      height: 6,
    },
    rightControls: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
  };

  return (
    <div style={styles.root}>
      <div style={styles.leftPane}>
        <div
          ref={containerRef}
          onClick={togglePlay}
          style={
            asset.type === BriefDeliverableTaskUploadTypeEnum.Image
              ? styles.containerImage
              : styles.containerMedia()
          }
        >
          {asset.type === BriefDeliverableTaskUploadTypeEnum.Video && (
            <video
              ref={videoRef}
              src={asset.url}
              style={styles.videoImg}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
              playsInline
            />
          )}

          {asset.type === BriefDeliverableTaskUploadTypeEnum.Image && (
            <img
              ref={imgRef}
              src={asset.url}
              style={styles.videoImg}
              alt="Asset"
              onLoad={(e) => {
                const target = e.currentTarget;
                if (target.naturalWidth && target.naturalHeight) {
                  setMediaAspect(target.naturalWidth / target.naturalHeight);
                }
                resizeCanvas();
              }}
            />
          )}

          {asset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
            (!ytError ? (
              <div
                ref={ytContainerRef}
                style={{ width: "100%", height: "100%", pointerEvents: "none" }}
              />
            ) : (
              <div
                style={{
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <AlertCircle
                  style={{
                    width: 40,
                    height: 40,
                    marginBottom: 8,
                    color: "#ff6b6b",
                  }}
                />
                <p style={{ margin: 0 }}>Failed to load YouTube video.</p>
                <p style={{ marginTop: 6, fontSize: 12, color: "#9ca3af" }}>
                  Check URL or connection.
                </p>
              </div>
            ))}

          {asset.type !== BriefDeliverableTaskUploadTypeEnum.MediaLink ? (
            <>
              <canvas
                ref={canvasRef}
                style={styles.canvas(isDrawingMode)}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={endDrawing}
                onMouseLeave={endDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={endDrawing}
              />

              {!isPlaying &&
                !isDrawingMode &&
                (asset.type === BriefDeliverableTaskUploadTypeEnum.Video ||
                  asset.type ===
                    BriefDeliverableTaskUploadTypeEnum.YoutubeLink) && (
                  <div style={styles.centerOverlay}>
                    <div style={styles.playIconWrap}>
                      <Play style={{ width: 32, height: 32, color: "#fff" }} />
                    </div>
                  </div>
                )}

              {isDrawingMode && (
                <div style={styles.drawingBadge}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: "#ef4444",
                      animation: "pulse 1.2s infinite",
                    }}
                  />
                  <span>Drawing Active</span>
                </div>
              )}
            </>
          ) : (
            <span style={{ color: "#fff" }}>
              Couldn't open the file in the player.{" "}
              <Button
                type="link"
                onClick={() => {
                  window.open(asset?.url, "_blank");
                }}
                style={{ padding: 0 }}
              >
                Open file
              </Button>
            </span>
          )}
        </div>

        {asset.type ===
        BriefDeliverableTaskUploadTypeEnum.Image ? null : asset.type ===
            BriefDeliverableTaskUploadTypeEnum.MediaLink ||
          duration === 0 ? null : (
          <div
            style={{
              background: "#111827",
              padding: "8px 16px",
              zIndex: 20,
              width: "100%",
              boxSizing: "border-box",
              borderRadius: "0 0 10px 10px",
            }}
          >
            <div
              ref={timelineRef}
              onClick={handleTimelineClick}
              onMouseEnter={() => !isDrawingMode && setIsHoveringTimeline(true)}
              onMouseLeave={() => {
                setIsHoveringTimeline(false);
                setHoverTime(null);
              }}
              onMouseMove={handleTimelineMouseMove}
              onTouchStart={handleTimelineTouchStart}
              onTouchMove={handleTimelineTouchMove}
              onTouchEnd={handleTimelineTouchEnd}
              style={{
                ...styles.timelineWrapper,
                cursor: isDrawingMode ? "not-allowed" : "pointer",
              }}
            >
              <div style={styles.timelineTrack}>
                <div
                  style={styles.progressBar(
                    (currentTime / (duration || 1)) * 100
                  )}
                />
                {addSegment.from !== null && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      height: "100%",
                      background: "#1e3a8a",
                      borderRadius: "2px",
                      zIndex: 20,
                      pointerEvents: "none",
                      left: `${((addSegment.from || 0) / (duration || 1)) * 100}%`,
                      width: addSegment.to
                        ? `${((addSegment.to - addSegment.from) / (duration || 1)) * 100}%`
                        : "4px",
                    }}
                  >
                    {addSegment.to && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          marginTop: "6px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: "9px",
                          fontWeight: 700,
                          color: "white",
                          background: "#1e3a8a",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          whiteSpace: "nowrap",
                          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        Integration
                      </div>
                    )}
                  </div>
                )}
              </div>

              {isHoveringTimeline && hoverTime !== null && !isDrawingMode && (
                <div
                  style={styles.hoverGhost((hoverTime / (duration || 1)) * 100)}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: -28,
                      left: "-50%",
                      transform: "translateX(-50%)",
                      fontSize: 12,
                      background: "#111827",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: 4,
                    }}
                  >
                    {formatTime(hoverTime)}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.controlsRow}>
              <div style={styles.controlsLeft}>
                <button
                  onClick={togglePlay}
                  disabled={isDrawingMode}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: isDrawingMode ? "default" : "pointer",
                    color: isDrawingMode ? "rgba(0,0,0,0.4)" : "inherit",
                  }}
                >
                  {isPlaying ? (
                    <Pause style={{ width: 20, height: 20 }} />
                  ) : (
                    <Play style={{ width: 20, height: 20 }} />
                  )}
                </button>

                <div style={styles.timeInfo}>
                  <span style={{ fontWeight: 600 }}>
                    {formatTime(currentTime)}
                  </span>
                  <span style={{}}>/</span>
                  <span>{formatTime(duration)}</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={styles.volumeContainer}>
                    <button
                      onClick={toggleMute}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      {isMuted ? (
                        <VolumeX
                          style={{ width: 20, height: 20, color: "#9ca3af" }}
                        />
                      ) : (
                        <Volume2
                          style={{ width: 20, height: 20, color: "#9ca3af" }}
                        />
                      )}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={isMuted ? 0 : volume}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setVolume(val);
                        setIsMuted(val === 0);
                        if (
                          asset.type ===
                            BriefDeliverableTaskUploadTypeEnum.Video &&
                          videoRef.current
                        )
                          videoRef.current.volume = val;
                        if (
                          asset.type ===
                            BriefDeliverableTaskUploadTypeEnum.YoutubeLink &&
                          ytPlayerRef.current &&
                          typeof ytPlayerRef.current.setVolume === "function"
                        )
                          ytPlayerRef.current.setVolume(val * 100);
                      }}
                      style={styles.range}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.rightControls}>
                <span
                  style={{
                    fontSize: 10,
                    color: "#9ca3af",
                    marginRight: 8,
                    display: "none",
                  }}
                >
                  Click timeline to comment
                </span>
                {/* Ad Segment Button - Only show when controls are hidden */}
                {!showSegmentControls && (
                  <button
                    onClick={() => setShowSegmentControls(true)}
                    disabled={isDrawingMode}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      border: hasSavedSegment
                        ? "1px solid rgba(245, 158, 11, 0.5)"
                        : "1px solid rgba(255, 255, 255, 0.1)",
                      background: hasSavedSegment
                        ? "rgba(245, 158, 11, 0.2)"
                        : "rgba(0, 0, 0, 0.4)",
                      backdropFilter: "blur(12px)",
                      cursor: isDrawingMode ? "not-allowed" : "pointer",
                      color: hasSavedSegment
                        ? "rgba(245, 158, 11, 1)"
                        : "rgba(255, 255, 255, 0.7)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (!isDrawingMode) {
                        e.currentTarget.style.background = hasSavedSegment
                          ? "rgba(245, 158, 11, 0.3)"
                          : "rgba(255, 255, 255, 0.1)";
                        e.currentTarget.style.color = "white";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = hasSavedSegment
                        ? "rgba(245, 158, 11, 0.2)"
                        : "rgba(0, 0, 0, 0.4)";
                      e.currentTarget.style.color = hasSavedSegment
                        ? "rgba(245, 158, 11, 1)"
                        : "rgba(255, 255, 255, 0.7)";
                    }}
                  >
                    <Scissors size={16} />
                  </button>
                )}

                {/* Segment Controls - Only show when button is clicked */}
                {showSegmentControls && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      background: "rgba(0, 0, 0, 0.4)",
                      backdropFilter: "blur(12px)",
                      borderRadius: 8,
                      padding: 4,
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <button
                      onClick={handleSetIntegrationStart}
                      disabled={isDrawingMode}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "all 0.2s",
                        border:
                          addSegment.from !== null
                            ? "1px solid #1e3a8a"
                            : "none",
                        cursor: isDrawingMode ? "not-allowed" : "pointer",
                        background:
                          addSegment.from !== null ? "#1e3a8a" : "transparent",
                        color:
                          addSegment.from !== null
                            ? "white"
                            : "rgba(255, 255, 255, 0.7)",
                      }}
                      onMouseEnter={(e) => {
                        if (!isDrawingMode && addSegment.from === null) {
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.color = "white";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (addSegment.from === null) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color =
                            "rgba(255, 255, 255, 0.7)";
                        }
                      }}
                    >
                      <GripHorizontal size={12} />{" "}
                      {addSegment.from !== null
                        ? formatTime(addSegment.from)
                        : "Set Start"}
                    </button>
                    <div
                      style={{
                        width: 1,
                        height: 16,
                        background: "rgba(255, 255, 255, 0.1)",
                      }}
                    />
                    <button
                      onClick={handleSetIntegrationEnd}
                      disabled={isDrawingMode || addSegment.from === null}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "all 0.2s",
                        border:
                          addSegment.to !== null ? "1px solid #1e3a8a" : "none",
                        cursor:
                          isDrawingMode || addSegment.from === null
                            ? "not-allowed"
                            : "pointer",
                        background:
                          addSegment.to !== null ? "#1e3a8a" : "transparent",
                        color:
                          addSegment.to !== null
                            ? "white"
                            : addSegment.from === null
                              ? "rgba(255, 255, 255, 0.3)"
                              : "rgba(255, 255, 255, 0.7)",
                      }}
                      onMouseEnter={(e) => {
                        if (
                          !isDrawingMode &&
                          addSegment.from !== null &&
                          addSegment.to === null
                        ) {
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.color = "white";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (addSegment.to === null) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color =
                            addSegment.from === null
                              ? "rgba(255, 255, 255, 0.3)"
                              : "rgba(255, 255, 255, 0.7)";
                        }
                      }}
                    >
                      {addSegment.to !== null
                        ? formatTime(addSegment.to)
                        : "Set End"}{" "}
                      <GripHorizontal size={12} />
                    </button>
                    <button
                      onClick={handleClearIntegration}
                      disabled={isDrawingMode}
                      style={{
                        marginLeft: 4,
                        padding: 6,
                        borderRadius: 4,
                        border: "none",
                        background: "transparent",
                        cursor: isDrawingMode ? "not-allowed" : "pointer",
                        color: "rgba(255, 255, 255, 0.5)",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        if (!isDrawingMode) {
                          e.currentTarget.style.background =
                            "rgba(239, 68, 68, 0.2)";
                          e.currentTarget.style.color = "#f87171";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color =
                          "rgba(255, 255, 255, 0.5)";
                      }}
                    >
                      <X size={12} />
                    </button>
                    {addSegment.from !== null && addSegment.to !== null && (
                      <button
                        onClick={handleSaveSegment}
                        disabled={
                          isDrawingMode ||
                          isAddAdSegmentLoading ||
                          addSegment.from === null ||
                          addSegment.to === null
                        }
                        style={{
                          marginLeft: 4,
                          padding: 6,
                          borderRadius: 4,
                          border: "none",
                          background: "transparent",
                          cursor:
                            isDrawingMode ||
                            isAddAdSegmentLoading ||
                            addSegment.from === null ||
                            addSegment.to === null
                              ? "not-allowed"
                              : "pointer",
                          color: "rgba(255, 255, 255, 0.5)",
                          transition: "all 0.2s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity:
                            isDrawingMode ||
                            isAddAdSegmentLoading ||
                            addSegment.from === null ||
                            addSegment.to === null
                              ? 0.5
                              : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (
                            !isDrawingMode &&
                            !isAddAdSegmentLoading &&
                            addSegment.from !== null &&
                            addSegment.to !== null
                          ) {
                            e.currentTarget.style.background =
                              "rgba(34, 197, 94, 0.2)";
                            e.currentTarget.style.color = "#4ade80";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color =
                            "rgba(255, 255, 255, 0.5)";
                        }}
                      >
                        {isAddAdSegmentLoading ? (
                          <Loader2
                            size={12}
                            style={{
                              animation: "spin 1s linear infinite",
                            }}
                          />
                        ) : (
                          <Check size={12} />
                        )}
                      </button>
                    )}
                  </div>
                )}
                {/* <Button
                  icon={
                    <Captions
                      size={16}
                      color={
                        showTranscription
                          ? "oklch(67.3% 0.182 276.935)"
                          : "gray"
                      }
                      style={{
                        cursor: isDrawingMode ? "default" : "pointer",
                        borderRadius: 5,
                      }}
                    />
                  }
                  onClick={() => setShowTranscription((prev) => !prev)}
                  type="text"
                  style={{
                    padding: 5,
                  }}
                /> */}
              </div>
            </div>
          </div>
        )}
      </div>
      {showTranscription && (
        <aside style={styles.rightPanel} aria-label="Player right panel">
          <Flex vertical gap={15}>
            <Card
              size="small"
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#d1d5db",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      margin: 0,
                    }}
                  >
                    Transcript
                  </div>
                  <Button
                    icon={<X size={14} color="#6b7280" />}
                    onClick={() => setShowTranscription(false)}
                    type="text"
                    style={{
                      padding: 5,
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                </div>
              }
              style={{
                background: "#1f2937", // gray-800
                border: "1px solid #374151", // gray-700
                borderRadius: 12,
                boxShadow:
                  "0 20px 25px -5px rgba(0,0,0,0.35), 0 8px 10px -6px rgba(0,0,0,0.25)",
                overflow: "hidden",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
              headStyle={{
                background: "transparent",
                borderBottom: "1px solid #374151",
                padding: "4px 16px",
                minHeight: "auto",
              }}
              bodyStyle={{
                padding: 0,
                flex: 1,
                minHeight: 0,
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              {transcriptionLoading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 24,
                  }}
                >
                  <Spin tip="Generating transcription..." />
                </div>
              ) : hasTranscription ? (
                transcription?.map((segment) => (
                  <div
                    key={segment._id}
                    onClick={() => seekTo(segment.start)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      padding: 12,
                      borderBottom: "1px solid rgba(55, 65, 81, 0.5)", // gray-700/50
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background =
                        "rgba(255,255,255,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background =
                        "transparent";
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        color: "#6b7280",
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      }}
                    >
                      {formatTime(segment.start)} - {formatTime(segment.end)}
                    </span>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#d1d5db",
                        margin: 0,
                      }}
                    >
                      {segment.text}
                    </p>
                  </div>
                ))
              ) : (
                <div style={{ color: "#9ca3af", padding: 12 }}>
                  No transcription available yet.
                </div>
              )}
            </Card>
          </Flex>
        </aside>
      )}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default VideoPlayer;
